import { computed, reactive, ref, type Ref } from "vue";
import { displayPagingRange } from "@/common/common";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
  localStorageGetPageHeaders,
} from "@/common/localStorageUtil";
import { listTmpMember, getTmpMemberCount } from "@/common/graphql/tmpMember";
import type {
  TmpMemberListInput,
  TmpMemberCountInput,
  OperationInput,
} from "@/generated/graphql";
import type { TableHeader, ListData, ListOptions } from "../types";
import { TMP_MEMBER_LIST_FILE_NAME } from "../constants";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseTmpMemberTableParams {
  searchConditionName: Ref<string | undefined | null>;
  searchConditionPhone: Ref<string | undefined | null>;
  searchConditionZipCode: Ref<string | undefined | null>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  displayListSearchDialog: Ref<boolean>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useTmpMemberTable = (params: UseTmpMemberTableParams) => {
  const {
    searchConditionName,
    searchConditionPhone,
    searchConditionZipCode,
    displayListSearchDialog,
  } = params;

  // 一覧表示
  const defaultTableHeaders = ref<TableHeader[]>([
    { id: 1, display: true, title: "お名前", text: "お名前", settingText: "お名前", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "name", value: "name" },
    { id: 2, display: true, title: "お名前(かな)", text: "お名前(かな)", settingText: "お名前(かな)", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "name_kana", value: "name_kana" },
    { id: 3, display: true, title: "郵便番号", text: "郵便番号", settingText: "郵便番号", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "zip_code", value: "zip_code" },
    { id: 4, display: true, title: "住所", text: "住所", settingText: "住所", align: "left", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "address", value: "address" },
    { id: 5, display: true, title: "電話番号", text: "電話番号", settingText: "電話番号", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "phone", value: "phone" },
    { id: 6, display: true, title: "", text: "", settingText: "(操作)", align: "center", sortable: false, orderDisabled: true, order: "", orderKey: "", key: "actions", value: "actions" },
  ]);

  const tableHeaders = ref<TableHeader[]>([]);
  const listLoading = ref(false);
  const listOptions = reactive<ListOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
    groupBy: [],
    groupDesc: [],
    multiSort: false,
    mustSort: false,
  });

  const listDataArray = reactive<ListData[]>([]);
  const listDataTotalCount = ref(0);
  const displayPagingRangeText = ref("");

  const pageCount = computed(() => Math.ceil(listDataTotalCount.value / listOptions.itemsPerPage));

  const filteredTableHeaders = (): TableHeader[] => {
    return tableHeaders.value.filter((value) => value.display);
  };

  const initTableHeaders = (): void => {
    const tableHeadersStr: string | null = localStorageGetPageHeaders(TMP_MEMBER_LIST_FILE_NAME);
    if (tableHeadersStr === null) {
      tableHeaders.value = JSON.parse(JSON.stringify(defaultTableHeaders.value)) as TableHeader[];
    } else {
      const parsedHeaders = JSON.parse(tableHeadersStr) as TableHeader[];
      tableHeaders.value = parsedHeaders.map((header) => ({
        ...header,
        title: header.title ?? header.text,
        key: header.key ?? header.value,
      }));
    }
  };

  const searchQuery = async (): Promise<void> => {
    const listInput: TmpMemberListInput = {
      kana: searchConditionName.value !== undefined && searchConditionName.value !== null && searchConditionName.value !== "" ? searchConditionName.value : undefined,
      phone: searchConditionPhone.value !== undefined && searchConditionPhone.value !== null && searchConditionPhone.value !== "" ? searchConditionPhone.value : undefined,
      zip_code: searchConditionZipCode.value !== undefined && searchConditionZipCode.value !== null && searchConditionZipCode.value !== "" ? searchConditionZipCode.value : undefined,
      chain_code: localStorageGetChainCode(),
      shop_code: localStorageGetShopCode(),
      customer_code: localStorageGetCustomerCode(),
      limit: listOptions.itemsPerPage,
      offset: (listOptions.page - 1) * listOptions.itemsPerPage,
    };
    const opInput: OperationInput = { area_code: "10", detail: "仮会員一覧取得", display_category: "仮会員一覧", operation: "", user_id: localStorageGetUserId() };
    const countInput: TmpMemberCountInput = { chain_code: listInput.chain_code, customer_code: listInput.customer_code, shop_code: listInput.shop_code, kana: listInput.kana, phone: listInput.phone, zip_code: listInput.zip_code };
    const list = await listTmpMember(listInput, opInput);
    listDataArray.length = 0;
    list!.forEach((value) => {
      if (value !== undefined && value !== null) {
        listDataArray.push({ tmpMemberId: value.tmp_member_id, name: value.name1 + value.name2, name_kana: value.name_kana1 + value.name_kana2, zip_code: value.zip_code, address: value.address1 + value.address2 + (value.address3 ? value.address3 : ""), phone: value.phone, is_deliverable: value.is_deliverable });
      }
    });
    listDataTotalCount.value = await getTmpMemberCount(countInput);
    displayPagingRangeText.value = displayPagingRange(listOptions.page - 1, listOptions.itemsPerPage, listDataTotalCount.value);
  };

  const onClickSearch = async (): Promise<void> => {
    listOptions.page = 1;
    await searchQuery();
    displayListSearchDialog.value = false;
  };

  const onUpdateOptions = async (options: { page: number; itemsPerPage: number }): Promise<void> => {
    listOptions.page = options.page;
    listOptions.itemsPerPage = options.itemsPerPage;
    listLoading.value = true;
    await searchQuery();
    listLoading.value = false;
  };

  return {
    defaultTableHeaders,
    tableHeaders,
    listLoading,
    listOptions,
    listDataArray,
    listDataTotalCount,
    displayPagingRangeText,
    pageCount,
    filteredTableHeaders,
    initTableHeaders,
    searchQuery,
    onClickSearch,
    onUpdateOptions,
  };
};
